import { joiResolver } from "@hookform/resolvers/joi";
import { Select } from "flowbite-react";
import Joi from "joi";
import { useForm } from "react-hook-form";
import useAuth from "../../../../hooks/useAuth";
import { useTranslation } from "react-i18next";
import {
  useAddUsersToSpecifiedGroup,
  useGetGroups,
} from "../../../../hooks/groups";
import { CustomButton } from "../../../atoms/CustomButton";
import { ModalShell } from "../../../atoms/ModalShell";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

type AddToGroupModalProps = {
  userList: Set<string>;
  show: boolean;
  onHide: () => void;
  onResolved: () => void;
};

const schema = Joi.object({
  group_identification: Joi.string().trim().required().label("Group"),
});
const AddToGroupModal = ({
  userList,
  show,
  onHide,
  onResolved,
}: AddToGroupModalProps) => {
  const [t] = useTranslation();

  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canAddToGroup = permissions.can(Cap.USERS_UPDATE, Level.WRITE);
  const { register, handleSubmit } = useForm({
    resolver: joiResolver(schema),
  });
  const { data: groupsRes } = useGetGroups({
    customer_identification: customer_identification,
    entityIdentification: user.entity_identification,
    limit: 50,
    sortOrder: "DESC",
    pageParam: 0,
    enabled: show && permissions.can(Cap.GROUP_MANAGEMENT, Level.VIEW),
  });

  const { mutate: addUsersToGroup, isLoading } = useAddUsersToSpecifiedGroup();

  const onSubmit = (values: any) => {
    addUsersToGroup(
      {
        customer_identification: customer_identification,
        entityIdentification: user.entity_identification,
        group_identification: values.group_identification,
        users: Array.from(userList),
      },
      {
        onSuccess: () => {
          onResolved();
          onHide();
        },
      }
    );
  };

  return (
    <ModalShell
      show={show}
      onClose={() => {
        onResolved();
        onHide();
      }}
      size="lg"
      title={t("dashboard.entities.viewEntityUser.key14")}
      subtitle={t("dashboard.entities.viewEntityUser.key15")}
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <CustomButton
          icon="solidPlusCircle"
          type="submit"
          disabled={
            groupsRes?.groups.length <= 0 || isLoading || !canAddToGroup
          }
          variant="primary"
          isLoading={isLoading}
          title={t("common.add")}
          className="whitespace-nowrap"
        />
      }
    >
      <Select
        translate="no"
        aria-label="ownerGroups"
        className="w-full"
        {...register("group_identification")}
      >
        <option value="">
          {t("dashboard.entities.manageCustomerGroups.selectText")}
        </option>
        {groupsRes?.groups?.map((gr: any, index: number) => (
          <option value={gr.group_identification} key={index}>
            {gr.group_name}
          </option>
        ))}
      </Select>
    </ModalShell>
  );
};

export default AddToGroupModal;
